// MEETING orchestrator (singleton) — full-mesh group calls.
//
// A "room" holds N participants; this client keeps ONE RTCPeerConnection per
// other participant (a 1-to-1 call is just a room of 2). Glare-free rule: when
// I JOIN (or REJOIN) a room I create the offer to every present member; when
// someone joins after me, THEY offer to me and I only answer.
//
// Staying connected (what used to make calls "drop"):
//   - an ICE link that goes `disconnected` is NOT torn down: after a short
//     grace it gets an ICE restart (driven by the lower userId of the pair so
//     both sides never offer at once; the other side asks for it);
//   - a lost signaling socket does not end the call either: the server keeps
//     the seat (room:peer-away) and this tab sends room:rejoin as soon as the
//     socket is back, then re-offers to everyone;
//   - the roomId is kept in sessionStorage, so a reloaded tab can get back
//     into the call with the "Rejoin" button (state.rejoinable).
//
// Reactive UI state lives in `meeting.state` (Vue.observable). MediaStream /
// RTCPeerConnection objects are kept OUT of it (Vue 2 would corrupt them) —
// components read them via getLocalPreviewStream()/getParticipantStream(id)
// and re-bind when `state.streamTick` changes.

import Vue from 'vue'
import { connectSocket, getSocket, disconnectSocket } from '@/socket.js'
import {
  getIceServers, getLocalMedia, getScreenMedia, createPeerConnection, stopStream,
  videoSender, audioSender, applyVideoParams, loadDevicePrefs, saveDevicePrefs
} from '@/services/webrtc.js'
import { startRingback, stopRingback } from '@/services/ringtone.js'

const ROOM_KEY = 'meeting-room' // sessionStorage: roomId of the call this tab is in
const RING_TIMEOUT_MS = 50 * 1000 // client-side safety net (server rings 45 s)
const UNSTABLE_GRACE_MS = 3000 // ICE 'disconnected' → wait this long before restarting
const RECOVER_RETRY_MS = 6000 // between two ICE restart attempts on the same peer
const MAX_ICE_RESTARTS = 4
const CAMERA_BITRATE = 1200000
const SCREEN_BITRATE = 2500000

const myId = () => Number(localStorage.getItem('login-id')) || null

const IDLE_ROOM = () => ({
  status: 'idle', // idle | ringing | active
  roomId: null,
  inviter: null, // { id, name, avatar } — for the incoming popup
  startedAt: null // ms — for the duration counter
})

const state = Vue.observable({
  socketConnected: false,
  onlineUsers: [], // [{ userId, username, avatar, busy }] excluding me
  room: IDLE_ROOM(),
  // [{ userId, username, avatar, connected, link, away, mic, cam, sharing }]
  //   link: new | ok | unstable | lost   (ICE health of my connection to them)
  //   away: their signaling socket is gone, seat kept by the server
  participants: [],
  mic: true,
  cam: true,
  hasCamera: true,
  sharing: false,
  streamTick: 0,
  confirmLeave: false, // true while the "leave the call?" confirmation is up
  rejoinable: null, // { roomId, names: [] } — a call this tab can get back into
  pendingInvites: [], // [{ userId, username, avatar }] people I called who have not answered yet
  devices: loadDevicePrefs(), // { audioIn, videoIn, audioOut } chosen in the device check
  ui: { callPanel: false, deviceCheck: false, whatsNew: false }, // global popups, opened from anywhere
  notice: null // { text, color, ts } — transient toast (watched in Main.vue)
})

// Non-reactive call internals.
const peers = new Map() // userId -> { pc, stream, pending: [], restarts, restartedAt, timer }
let localStream = null
let cameraTrack = null
let screenStream = null
let screenTrack = null
let ringTimer = null
let incomingNotification = null // desktop Notification while an invite rings in a background tab
let initialized = false

let noticeSeq = 0
function notify (text, color) {
  state.notice = { text, color: color || 'warning', ts: ++noticeSeq }
}

function bump () { state.streamTick++ }

function emit (event, payload, ack) {
  const s = getSocket()
  if (s) s.emit(event, payload, ack)
}

function nameOf (id) {
  const p = state.participants.find(x => x.userId === id)
  return (p && p.username) || 'A participant'
}

function setParticipant (id, patch) {
  const i = state.participants.findIndex(p => p.userId === id)
  if (i === -1) {
    state.participants.push({
      userId: id,
      username: '',
      avatar: null,
      connected: false,
      link: 'new',
      away: false,
      mic: true,
      cam: true,
      sharing: false,
      ...patch
    })
  } else {
    Vue.set(state.participants, i, { ...state.participants[i], ...patch })
  }
}

function removeParticipant (id) {
  const i = state.participants.findIndex(x => x.userId === id)
  if (i !== -1) state.participants.splice(i, 1)
}

// Close my connection to one participant (they stay listed).
function closePeer (id) {
  const p = peers.get(id)
  if (!p) return
  clearTimeout(p.timer)
  try {
    p.pc.onconnectionstatechange = null
    p.pc.ontrack = null
    p.pc.onicecandidate = null
    p.pc.close()
  } catch (e) { /* noop */ }
  peers.delete(id)
}

function removePeer (id) {
  closePeer(id)
  removeParticipant(id)
  bump()
}

function setRoomId (id) {
  state.room.roomId = id
  try {
    if (id) sessionStorage.setItem(ROOM_KEY, id)
    else sessionStorage.removeItem(ROOM_KEY)
  } catch (e) { /* private mode */ }
}

function closeIncomingNotification () {
  if (incomingNotification) { try { incomingNotification.close() } catch (e) { /* noop */ } incomingNotification = null }
}

// Desktop notification for an incoming call when the ERP tab is not visible
// (permission is asked from the call panel / device check, never by surprise).
function showIncomingNotification (name) {
  try {
    if (!('Notification' in window) || Notification.permission !== 'granted' || !document.hidden) return
    closeIncomingNotification()
    incomingNotification = new Notification('Incoming call', {
      body: `${name} is calling you on XYLO`,
      tag: 'xylo-call',
      requireInteraction: true
    })
    incomingNotification.onclick = () => { try { window.focus() } catch (e) { /* noop */ } }
  } catch (e) { /* noop */ }
}

function teardown () {
  Array.from(peers.keys()).forEach(closePeer)
  peers.clear()
  clearTimeout(ringTimer); ringTimer = null
  if (screenStream) { stopStream(screenStream); screenStream = null; screenTrack = null }
  stopStream(localStream)
  localStream = null
  cameraTrack = null
  state.participants = []
  state.pendingInvites = []
  stopRingback()
  closeIncomingNotification()
  setRoomId(null)
  state.room = IDLE_ROOM()
  state.mic = true
  state.cam = true
  state.hasCamera = true
  state.sharing = false
  state.confirmLeave = false
  bump()
}

// Browser-level guard: closing / reloading the tab during a call interrupts
// it for everyone (a reload can come back with "Rejoin", a close cannot), so
// ask the browser to confirm first. Returning a string is what arms it.
function onBeforeUnload (e) {
  if (state.room.status !== 'active' || !state.participants.length) return undefined
  const msg = 'You are still in a call. Are you sure you want to leave?'
  e.preventDefault()
  e.returnValue = msg
  return msg
}

async function ensureLocalMedia () {
  if (localStream) return
  localStream = await getLocalMedia(state.devices) // camera + mic, or mic only
  cameraTrack = localStream.getVideoTracks()[0] || null
  state.hasCamera = !!cameraTrack
  if (!cameraTrack) state.cam = false
  localStream.getAudioTracks().forEach(t => { t.enabled = state.mic })
  localStream.getVideoTracks().forEach(t => { t.enabled = state.cam })
  bump()
}

// What my video slot currently carries (screen wins over camera), so a peer
// who joins DURING a screen share gets the screen too.
function outgoingVideoTrack () {
  return (state.sharing && screenTrack) || cameraTrack || null
}

function broadcastState () {
  if (state.room.status !== 'active' || !state.room.roomId) return
  emit('room:state', { roomId: state.room.roomId, mic: state.mic, cam: state.cam, sharing: state.sharing })
}

function tuneVideo (pc) {
  applyVideoParams(videoSender(pc), {
    maxBitrate: state.sharing ? SCREEN_BITRATE : CAMERA_BITRATE,
    screen: state.sharing
  })
}

// The caller hears a ringback while nobody has picked up yet.
function updateRingback () {
  const ringing = state.room.status === 'active' && state.participants.length === 0 && state.pendingInvites.length > 0
  if (ringing) startRingback()
  else stopRingback()
}

function addPending (user) {
  const id = Number(user.userId)
  if (!state.pendingInvites.some(p => p.userId === id)) {
    state.pendingInvites.push({ userId: id, username: user.username || '', avatar: user.avatar || null })
  }
  updateRingback()
}

function removePending (id) {
  id = Number(id)
  const i = state.pendingInvites.findIndex(p => p.userId === id)
  if (i !== -1) state.pendingInvites.splice(i, 1)
  updateRingback()
}

// ---- Peer connection lifecycle --------------------------------------------

async function restartIce (id) {
  const p = peers.get(id)
  if (!p) return
  try {
    // Fresh TURN credentials in case the old ones expired during the call.
    if (typeof p.pc.setConfiguration === 'function') {
      try { p.pc.setConfiguration({ iceServers: await getIceServers(), bundlePolicy: 'max-bundle' }) } catch (e) { /* unsupported */ }
    }
    if (typeof p.pc.restartIce === 'function') p.pc.restartIce()
    const offer = await p.pc.createOffer({ iceRestart: true })
    await p.pc.setLocalDescription(offer)
    emit('webrtc:offer', { toUserId: id, roomId: state.room.roomId, data: offer })
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[meeting] ICE restart failed', e && e.message)
  }
}

// Try to bring a broken link back with an ICE restart, a bounded number of
// times. The lower userId of the pair drives the restart; the other side
// asks for it (webrtc:restart) so the two never offer at the same time.
function recoverPeer (id) {
  const p = peers.get(id)
  if (!p || state.room.status !== 'active') return
  const st = p.pc.connectionState
  if (st === 'connected' || st === 'closed') return
  if (p.restarts >= MAX_ICE_RESTARTS) { setParticipant(id, { link: 'lost' }); return }
  if (Date.now() - p.restartedAt >= UNSTABLE_GRACE_MS) {
    p.restarts++
    p.restartedAt = Date.now()
    if (myId() < id) restartIce(id)
    else emit('webrtc:restart', { toUserId: id, roomId: state.room.roomId })
  }
  clearTimeout(p.timer)
  p.timer = setTimeout(() => recoverPeer(id), RECOVER_RETRY_MS)
}

function onPeerState (id, cs) {
  const p = peers.get(id)
  if (!p) return
  if (cs === 'connected') {
    clearTimeout(p.timer); p.timer = null
    p.restarts = 0
    setParticipant(id, { connected: true, link: 'ok' })
    tuneVideo(p.pc)
    bump()
  } else if (cs === 'disconnected') {
    // Transient most of the time (Wi-Fi hiccup, laptop lid): give it a moment.
    setParticipant(id, { link: 'unstable' })
    clearTimeout(p.timer)
    p.timer = setTimeout(() => {
      if (p.pc.connectionState === 'disconnected') recoverPeer(id)
    }, UNSTABLE_GRACE_MS)
  } else if (cs === 'failed') {
    setParticipant(id, { link: 'unstable' })
    recoverPeer(id)
  }
  // 'closed' is only ever our own doing (closePeer) — nothing to do.
}

async function createPeer (peer, initiator) {
  const id = Number(peer.userId)
  if (!id || peers.has(id)) return
  const iceServers = await getIceServers()
  if (state.room.status !== 'active' || peers.has(id)) return // call ended / raced meanwhile

  const remote = new MediaStream()
  const entry = { pc: null, stream: remote, pending: [], restarts: 0, restartedAt: 0, timer: null }
  const pc = createPeerConnection({
    iceServers,
    onIceCandidate: (candidate) => emit('webrtc:ice', { toUserId: id, roomId: state.room.roomId, data: candidate }),
    onTrack: (track) => {
      // One MediaStream per peer, fed track by track: replaceTrack() on their
      // side (camera ↔ screen) keeps the same track object, nothing to rebind.
      if (!remote.getTracks().includes(track)) remote.addTrack(track)
      bump()
    },
    onStateChange: (cs) => onPeerState(id, cs)
  })
  entry.pc = pc
  peers.set(id, entry)
  const known = state.participants.find(p => p.userId === id) || {}
  setParticipant(id, {
    username: peer.username || known.username || '',
    avatar: peer.avatar || known.avatar || null,
    connected: false,
    link: 'new',
    away: false
  })

  // One audio slot + one video slot are ALWAYS negotiated, even without a
  // camera: a later screen share is then a plain replaceTrack(), no renegotiation.
  const audioTrack = localStream ? localStream.getAudioTracks()[0] : null
  const videoTrack = outgoingVideoTrack()
  if (audioTrack) pc.addTrack(audioTrack, localStream)
  else pc.addTransceiver('audio', { direction: 'sendrecv' })
  if (videoTrack) pc.addTrack(videoTrack, localStream)
  else pc.addTransceiver('video', { direction: 'sendrecv' })

  if (initiator) {
    try {
      const offer = await pc.createOffer()
      await pc.setLocalDescription(offer)
      emit('webrtc:offer', { toUserId: id, roomId: state.room.roomId, data: offer })
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[meeting] offer failed', e && e.message)
    }
  }
}

async function flushPending (id) {
  const p = peers.get(id)
  if (!p) return
  const list = p.pending; p.pending = []
  for (const c of list) { try { await p.pc.addIceCandidate(c) } catch (e) { /* ignore */ } }
}

// ---- Public actions --------------------------------------------------------

// Call a colleague, OR — if already in a room — invite them into it.
async function callOrInvite (user) {
  if (state.room.status === 'active') {
    emit('room:invite', { toUserId: Number(user.userId), roomId: state.room.roomId }, (res) => {
      if (res && res.ok) return
      removePending(user.userId)
    })
    addPending(user)
    notify(`Inviting ${user.username}…`, 'primary')
    return
  }
  if (state.room.status !== 'idle') return
  try {
    await ensureLocalMedia()
  } catch (e) {
    notify('Microphone unavailable — allow it in the browser to call', 'danger')
    return
  }
  state.rejoinable = null
  state.room = { ...IDLE_ROOM(), status: 'active', startedAt: Date.now() }
  addPending(user)
  emit('room:invite', { toUserId: Number(user.userId) }, (res) => {
    if (!res || !res.ok) {
      const reason = (res && res.reason) || 'failed'
      if (reason === 'unavailable') notify(`${user.username} is not reachable right now`, 'warning')
      else if (reason === 'busy') notify(`${user.username} is already in a call`, 'warning')
      else notify('Could not start the call', 'danger')
      teardown()
      return
    }
    if (state.room.status === 'active') setRoomId(res.roomId)
  })
}

function rejectIncoming () {
  clearTimeout(ringTimer); ringTimer = null
  if (state.room.roomId) emit('room:reject', { roomId: state.room.roomId })
  teardown()
}

async function acceptIncoming () {
  if (state.room.status !== 'ringing') return
  clearTimeout(ringTimer); ringTimer = null
  const roomId = state.room.roomId
  try {
    await ensureLocalMedia()
  } catch (e) {
    notify('Microphone unavailable — allow it in the browser to answer', 'danger')
    rejectIncoming()
    return
  }
  state.rejoinable = null
  state.room = { ...IDLE_ROOM(), status: 'active', roomId, startedAt: Date.now() }
  setRoomId(roomId)
  emit('room:accept', { roomId })
}

function hangup () {
  const roomId = state.room.roomId
  const names = state.participants.map(p => p.username).filter(Boolean)
  if (roomId) {
    emit('room:leave', { roomId }, (res) => {
      // The others carry on without me: offer a way back in.
      if (res && res.roomAlive) state.rejoinable = { roomId, names }
    })
  }
  teardown()
}

// Every hang-up button goes through here: with people in the call it raises
// the in-app confirmation instead of dropping the call on a stray click.
function requestHangup () {
  if (state.room.status === 'active' && state.participants.length) { state.confirmLeave = true; return }
  hangup()
}

function cancelHangup () {
  state.confirmLeave = false
}

// Back into the call shown by the "Rejoin" chip (after a reload, or after
// leaving a group call that is still going on).
async function rejoin () {
  const target = state.rejoinable
  if (!target || state.room.status !== 'idle') return
  try {
    await ensureLocalMedia()
  } catch (e) {
    notify('Microphone unavailable — allow it in the browser to rejoin', 'danger')
    return
  }
  state.rejoinable = null
  state.room = { ...IDLE_ROOM(), status: 'active', roomId: target.roomId, startedAt: Date.now() }
  setRoomId(target.roomId)
  emit('room:rejoin', { roomId: target.roomId }, (res) => {
    if (res && res.ok) return // room:peers follows → we offer to everyone
    teardown()
    notify('That call has ended', 'warning')
  })
}

function dismissRejoin () {
  state.rejoinable = null
  try { sessionStorage.removeItem(ROOM_KEY) } catch (e) { /* noop */ }
}

// Signaling socket came back while in a call: take my seat again and
// re-offer to everyone (the server tells them to expect it).
function rejoinCurrent () {
  const roomId = state.room.roomId
  if (!roomId) return
  state.participants.forEach(p => setParticipant(p.userId, { link: 'unstable' }))
  emit('room:rejoin', { roomId }, (res) => {
    if (res && res.ok) return
    const had = state.participants.length
    teardown()
    if (had) notify('The call ended while you were offline', 'warning')
  })
}

// A tab that reloaded during a call: is that call still on?
function probeStoredRoom () {
  if (state.room.status !== 'idle') return
  let roomId = null
  try { roomId = sessionStorage.getItem(ROOM_KEY) } catch (e) { /* noop */ }
  if (!roomId) return
  emit('room:probe', { roomId }, (res) => {
    if (res && res.alive && state.room.status === 'idle') {
      state.rejoinable = { roomId, names: (res.members || []).map(m => m.username).filter(Boolean) }
    } else {
      try { sessionStorage.removeItem(ROOM_KEY) } catch (e) { /* noop */ }
    }
  })
}

// Use other devices (from the device check), live if a call is on: the new
// tracks replace the old ones on every peer, nobody has to reconnect.
async function applyDevices (prefs) {
  const next = { audioIn: prefs.audioIn || '', videoIn: prefs.videoIn || '', audioOut: prefs.audioOut || '' }
  saveDevicePrefs(next)
  state.devices = next
  bump() // speakers (sinkId) are applied by the video tiles
  if (!localStream) return
  let fresh = null
  try {
    fresh = await getLocalMedia(next)
  } catch (e) {
    notify('Could not switch devices', 'danger')
    return
  }
  const newAudio = fresh.getAudioTracks()[0] || null
  const newVideo = fresh.getVideoTracks()[0] || null
  for (const p of peers.values()) {
    const a = audioSender(p.pc)
    if (a && newAudio) { try { await a.replaceTrack(newAudio) } catch (e) { /* noop */ } }
    if (!state.sharing) {
      const v = videoSender(p.pc)
      if (v) { try { await v.replaceTrack(newVideo) } catch (e) { /* noop */ } }
    }
  }
  stopStream(localStream)
  localStream = fresh
  cameraTrack = newVideo
  state.hasCamera = !!newVideo
  if (!newVideo) state.cam = false
  localStream.getAudioTracks().forEach(t => { t.enabled = state.mic })
  localStream.getVideoTracks().forEach(t => { t.enabled = state.cam })
  broadcastState()
  bump()
}

function openCallPanel () { state.ui.callPanel = true }
function openDeviceCheck () { state.ui.deviceCheck = true }
function openWhatsNew () { state.ui.whatsNew = true }

function toggleMic () {
  state.mic = !state.mic
  if (localStream) localStream.getAudioTracks().forEach(t => { t.enabled = state.mic })
  broadcastState()
}

function toggleCam () {
  if (!state.hasCamera) return
  state.cam = !state.cam
  if (localStream) localStream.getVideoTracks().forEach(t => { t.enabled = state.cam })
  broadcastState()
}

async function replaceOutgoingVideo (track, screen) {
  for (const p of peers.values()) {
    const sender = videoSender(p.pc)
    if (!sender) continue
    try { await sender.replaceTrack(track || null) } catch (e) { /* noop */ }
    applyVideoParams(sender, { maxBitrate: screen ? SCREEN_BITRATE : CAMERA_BITRATE, screen })
  }
}

async function stopShareScreen () {
  if (!state.sharing) return
  state.sharing = false
  if (screenStream) { stopStream(screenStream); screenStream = null; screenTrack = null }
  await replaceOutgoingVideo(cameraTrack, false)
  broadcastState()
  bump()
}

async function shareScreen () {
  if (state.sharing || state.room.status !== 'active') return
  let screen = null
  try { screen = await getScreenMedia() } catch (e) { return } // picker cancelled
  screenStream = screen
  screenTrack = screen.getVideoTracks()[0]
  screenTrack.onended = () => stopShareScreen() // browser's own "Stop sharing" bar
  state.sharing = true
  await replaceOutgoingVideo(screenTrack, true)
  broadcastState()
  bump()
}

// ---- Socket signaling handlers --------------------------------------------

function wireSocket (s) {
  let hadSession = false

  s.on('connect', () => {
    state.socketConnected = true
    if (!hadSession) probeStoredRoom()
    else if (state.room.status === 'active' && state.room.roomId) rejoinCurrent()
    hadSession = true
  })
  s.on('disconnect', () => { state.socketConnected = false })

  s.on('presence:update', (list) => {
    const me = myId()
    state.onlineUsers = (list || []).filter(u => u.userId !== me)
  })

  s.on('room:incoming', ({ roomId, fromUserId, fromName, fromAvatar }) => {
    if (state.room.status !== 'idle') { s.emit('room:reject', { roomId }); return }
    state.room = {
      ...IDLE_ROOM(),
      status: 'ringing',
      roomId,
      inviter: { id: Number(fromUserId), name: fromName, avatar: fromAvatar }
    }
    showIncomingNotification(fromName)
    clearTimeout(ringTimer)
    ringTimer = setTimeout(() => {
      if (state.room.status === 'ringing' && state.room.roomId === roomId) {
        notify(`Missed call from ${fromName}`, 'warning')
        teardown()
      }
    }, RING_TIMEOUT_MS)
  })

  // The invite stopped ringing: answered on another tab, caller gave up, or timed out.
  s.on('room:incoming-cancel', ({ roomId, reason }) => {
    if (state.room.status !== 'ringing' || state.room.roomId !== roomId) return
    const name = state.room.inviter ? state.room.inviter.name : 'Someone'
    if (reason !== 'elsewhere') notify(`Missed call from ${name}`, 'warning')
    teardown()
  })

  // I joined (or rejoined): offer to every present member; keep a tile for away ones.
  s.on('room:peers', async ({ roomId, peers: list, rejoin: isRejoin }) => {
    if (state.room.status !== 'active' || roomId !== state.room.roomId) return
    if (isRejoin) {
      Array.from(peers.keys()).forEach(closePeer)
      state.participants.forEach(p => setParticipant(p.userId, { connected: false, link: 'new' }))
      bump()
    }
    for (const peer of (list || [])) {
      const id = Number(peer.userId)
      if (peer.away) {
        setParticipant(id, { username: peer.username, avatar: peer.avatar, connected: false, link: 'unstable', away: true })
      } else {
        await createPeer(peer, true)
      }
    }
    broadcastState()
  })

  // Someone joined after me: show a placeholder; they will offer to me.
  s.on('room:peer-joined', ({ userId, username, avatar }) => {
    if (state.room.status !== 'active') return
    removePending(userId)
    setParticipant(Number(userId), { username, avatar, connected: false, link: 'new', away: false })
    broadcastState()
  })

  // Someone came back (socket reconnect / reload): drop my old link, they re-offer.
  s.on('room:peer-rejoined', ({ userId, username, avatar }) => {
    if (state.room.status !== 'active') return
    const id = Number(userId)
    closePeer(id)
    setParticipant(id, { username, avatar, connected: false, link: 'new', away: false })
    bump()
    broadcastState()
  })

  // Their signaling socket is gone; the media link may well survive, keep it.
  s.on('room:peer-away', ({ userId }) => {
    if (state.room.status !== 'active') return
    setParticipant(Number(userId), { away: true })
  })

  s.on('room:peer-left', ({ userId }) => {
    const id = Number(userId)
    const name = nameOf(id)
    removePeer(id)
    updateRingback()
    if (state.room.status === 'active' && state.participants.length) notify(`${name} left the call`, 'dark')
  })

  s.on('room:peer-state', ({ userId, mic, cam, sharing }) => {
    if (state.room.status !== 'active') return
    setParticipant(Number(userId), { mic: !!mic, cam: !!cam, sharing: !!sharing })
  })

  s.on('room:ended', () => {
    const wasTalking = state.room.status === 'active' && state.participants.length > 0
    teardown()
    state.rejoinable = null
    if (wasTalking) notify('Call ended', 'dark')
  })
  s.on('room:unavailable', ({ toUserId } = {}) => {
    removePending(toUserId)
    notify('Contact not reachable', 'warning')
    if (!state.participants.length && !state.pendingInvites.length) teardown()
  })
  s.on('room:busy', ({ toUserId } = {}) => {
    removePending(toUserId)
    notify('Contact already in a call', 'warning')
    if (!state.participants.length && !state.pendingInvites.length) teardown()
  })
  s.on('room:invite-rejected', ({ byUserId, byName }) => {
    removePending(byUserId)
    notify(`${byName || 'The contact'} declined the call`, 'warning')
    if (!state.participants.length && !state.pendingInvites.length) teardown()
  })
  s.on('room:invite-timeout', ({ toUserId, name }) => {
    removePending(toUserId)
    notify(`${name || 'The contact'} does not answer`, 'warning')
    if (!state.participants.length && !state.pendingInvites.length) teardown()
  })

  s.on('webrtc:offer', async ({ fromUserId, data }) => {
    if (state.room.status !== 'active') return
    const id = Number(fromUserId)
    if (!peers.has(id)) {
      const known = state.participants.find(p => p.userId === id) || {}
      await createPeer({ userId: id, username: known.username, avatar: known.avatar }, false)
    }
    const p = peers.get(id)
    if (!p) return
    try {
      // Glare (both offered at once) only happens around an ICE restart:
      // drop my pending offer and take theirs.
      if (p.pc.signalingState !== 'stable') {
        await p.pc.setLocalDescription({ type: 'rollback' })
      }
      await p.pc.setRemoteDescription(data)
      await flushPending(id)
      const answer = await p.pc.createAnswer()
      await p.pc.setLocalDescription(answer)
      emit('webrtc:answer', { toUserId: id, roomId: state.room.roomId, data: answer })
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[meeting] offer handling failed', e && e.message)
    }
  })

  s.on('webrtc:answer', async ({ fromUserId, data }) => {
    const id = Number(fromUserId)
    const p = peers.get(id)
    if (!p || p.pc.signalingState !== 'have-local-offer') return // stale / duplicate
    try {
      await p.pc.setRemoteDescription(data)
      await flushPending(id)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[meeting] answer handling failed', e && e.message)
    }
  })

  s.on('webrtc:ice', async ({ fromUserId, data }) => {
    const p = peers.get(Number(fromUserId))
    if (!p) return
    if (!p.pc.remoteDescription || !p.pc.remoteDescription.type) { p.pending.push(data); return }
    try { await p.pc.addIceCandidate(data) } catch (e) { /* ignore */ }
  })

  // The other side of a broken link asks me (lower userId) to restart ICE.
  s.on('webrtc:restart', ({ fromUserId }) => {
    const id = Number(fromUserId)
    const p = peers.get(id)
    if (!p || state.room.status !== 'active' || myId() >= id) return
    if (Date.now() - p.restartedAt < UNSTABLE_GRACE_MS) return
    p.restartedAt = Date.now()
    restartIce(id)
  })
}

function init () {
  if (initialized) { connectSocket(); return }
  const s = connectSocket()
  if (!s) return
  initialized = true
  wireSocket(s)
  window.addEventListener('beforeunload', onBeforeUnload)
}

function shutdown () {
  teardown()
  state.rejoinable = null
  window.removeEventListener('beforeunload', onBeforeUnload)
  disconnectSocket()
  initialized = false
  state.socketConnected = false
  state.onlineUsers = []
}

export function getLocalStream () { return localStream }
// What my own tile shows: the shared screen while sharing, else the camera.
export function getLocalPreviewStream () { return (state.sharing && screenStream) || localStream }
export function getParticipantStream (id) { const p = peers.get(id); return p ? p.stream : null }

export default {
  state,
  init,
  shutdown,
  callOrInvite,
  acceptIncoming,
  rejectIncoming,
  requestHangup,
  cancelHangup,
  hangup,
  rejoin,
  dismissRejoin,
  applyDevices,
  openCallPanel,
  openDeviceCheck,
  openWhatsNew,
  toggleMic,
  toggleCam,
  shareScreen,
  stopShareScreen,
  getLocalStream,
  getLocalPreviewStream,
  getParticipantStream
}
